import { useMemo } from 'react';
import { Typography, Card, Row, Col, Spin, Tooltip } from 'antd';
import { ShopOutlined, DollarOutlined, TrophyOutlined, ShoppingOutlined, BarChartOutlined, ArrowRightOutlined } from '@ant-design/icons';
import './marketplace.css';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

const MarketplaceAnalytics = ({ salesData, topClientsData, loading, currentDateInfo }) => {
  const navigate = useNavigate();

  const handleGoToOrders = () => {
    navigate('/orders/all/1');
  };

  const metrics = useMemo(() => {
    if (!salesData || !topClientsData) {
      return {
        totalSales: 0,
        topClient: null,
        totalOrders: 0,
        topCategory: null
      };
    }

    const totalSales = salesData.total_sales || 0;
    const topClient = (topClientsData.top_clients || [])[0] || null;
    const totalOrders = salesData.total_orders || 0;
    const salesByCategory = salesData.sales_by_category || [];
    const topCategory = salesByCategory.length > 0
      ? salesByCategory.reduce((prev, current) =>
        (prev.total > current.total) ? prev : current
      )
      : null;

    return {
      totalSales,
      topClient,
      totalOrders,
      topCategory
    };
  }, [salesData, topClientsData]);

  if (loading) {
    return (
      <Card className="marketplace-card">
        <div className="card-header">
          <div className="card-icon card-icon-marketplace">
            <ShopOutlined />
          </div>
          <div>
            <Title level={3}>Analíticas de Órdenes</Title>
            <Text type="secondary">Resumen del período</Text>
          </div>
        </div>
        <div className="card-content">
          <Row gutter={[16, 16]}>
            {[1, 2, 3, 4].map((_, index) => (
              <Col span={6} key={index}>
                <div className="marketplace-metric-skeleton">
                  <Spin size="small" />
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </Card>
    );
  }

  return (
    <Card className="marketplace-card">
      <div className="card-header">
        <Tooltip
          title="Click aquí para ir a Órdenes Generales. Ver el detalle completo de todas las órdenes."
          placement="top"
          mouseEnterDelay={0.3}
        >
          <div
            className="card-icon card-icon-marketplace clickable-icon"
            onClick={handleGoToOrders}
            style={{ cursor: 'pointer', position: 'relative' }}
          >
            <ShopOutlined />
            <ArrowRightOutlined className="icon-hover-arrow" />
          </div>
        </Tooltip>
        <div>
          <Tooltip
            title="Click aquí para ir a Órdenes Generales. Ver el detalle completo de todas las órdenes."
            placement="top"
            mouseEnterDelay={0.3}
          >
            <Title
              level={3}
              style={{ cursor: 'pointer' }}
              onClick={handleGoToOrders}
              className="clickable-title"
            >
              Analíticas de Órdenes
            </Title>
          </Tooltip>
          <Text type="secondary">{currentDateInfo || "Resumen del período"}</Text>
        </div>
      </div>
      <div className="card-content">
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <div className="marketplace-metric">
              <div className="metric-icon-wrapper metric-icon-sales">
                <DollarOutlined />
              </div>
              <div className="metric-content">
                <div className="metric-value">
                  ${parseFloat(metrics.totalSales).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="metric-title">Ventas Totales</div>
              </div>
            </div>
          </Col>

          <Col span={6}>
            <div className="marketplace-metric">
              <div className="metric-icon-wrapper metric-icon-client">
                <TrophyOutlined />
              </div>
              <div className="metric-content">
                <div className="metric-value">
                  {metrics.topClient ? (
                    <>
                      <div className="client-name" title={metrics.topClient.name}>
                        {metrics.topClient.name?.length > 15
                          ? `${metrics.topClient.name.substring(0, 15)}...`
                          : metrics.topClient.name || 'N/A'}
                      </div>
                      <div className="client-sales">
                        ${parseFloat(metrics.topClient.total_sales || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </>
                  ) : (
                    <div className="no-data">Sin datos</div>
                  )}
                </div>
                <div className="metric-title">Cliente Principal</div>
              </div>
            </div>
          </Col>

          <Col span={6}>
            <div className="marketplace-metric">
              <div className="metric-icon-wrapper metric-icon-orders">
                <ShoppingOutlined />
              </div>
              <div className="metric-content">
                <div className="metric-value">{metrics.totalOrders}</div>
                <div className="metric-title">Órdenes Totales</div>
              </div>
            </div>
          </Col>

          <Col span={6}>
            <div className="marketplace-metric">
              <div className="metric-icon-wrapper metric-icon-category">
                <BarChartOutlined />
              </div>
              <div className="metric-content">
                <div className="metric-value">
                  {metrics.topCategory ? (
                    <>
                      <div className="category-name" title={metrics.topCategory.name}>
                        {metrics.topCategory.name?.length > 15
                          ? `${metrics.topCategory.name.substring(0, 15)}...`
                          : metrics.topCategory.name || 'N/A'}
                      </div>
                      <div className="category-sales">
                        ${parseFloat(metrics.topCategory.total || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </>
                  ) : (
                    <div className="no-data">Sin datos</div>
                  )}
                </div>
                <div className="metric-title">Categoría Top</div>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </Card>
  );
};

export default MarketplaceAnalytics;